import { c as _c } from "react/compiler-runtime";
import { CAROUSEL_DURATION_MS, isVODStatusFailed, MEDIA_TYPE } from '@/hooks/useTrailerViewMedia';
import { getStreamUris } from '@/lib/blendvision';
import { createPlaybackToken } from '@/server/services/blendvision';
import { Vod } from '@/server/services/types';
import { getFile, getInitialTrailerVod } from '@/services/blendvision';
import { fetchTrailerMedia } from '@/services/cms';
import { TrailerViewVideoData } from '@/types/asset';
import useSWR from 'swr';
const buildPlayableVideoData = ({
  vod,
  download_uri,
  token,
  data
}: {
  vod: Vod;
  download_uri: string;
  token: string | undefined;
  data: {
    fileId: string;
    vodId: string;
  };
}): TrailerViewVideoData => {
  const {
    dashUri,
    hlsUri
  } = getStreamUris(vod.stream);
  return {
    fileId: data?.fileId,
    vodId: data?.vodId,
    url: download_uri || '',
    playbackToken: token || undefined,
    coverImage: vod?.cover_image?.auto?.url || '',
    vodStatus: vod?.status || '',
    isConverted: vod?.status === 'VOD_STATUS_SUCCEEDED',
    type: 'video',
    title: vod.name,
    ...(dashUri && {
      dashUri
    }),
    ...(hlsUri && {
      hlsUri
    })
  };
};
const buildImageData = (image: {
  url?: string;
}) => {
  return {
    url: image?.url || '',
    duration: CAROUSEL_DURATION_MS,
    type: 'image' as const
  };
};
const convertInitialVideos = async (trailerVideos: Array<{
  vod_id: string;
  file_id: string;
  cover_image?: string;
}>): Promise<TrailerViewVideoData[]> => {
  if (!Array.isArray(trailerVideos)) return [];
  const videoData = await Promise.all(trailerVideos.map(async ({
    vod_id,
    file_id
  }) => {
    const vodResponse = await getInitialTrailerVod(vod_id);
    if (!('vod' in vodResponse)) {
      console.log(`[Trailer Media] Missing vod from Web Proxy: vod_id: ${vod_id}, file_id: ${file_id}`);
      return undefined;
    }
    const vod = vodResponse.vod;
    const {
      download_uri
    } = await getFile(file_id);
    if (vod?.status === 'VOD_STATUS_SUCCEEDED') {
      const playbackToken = await createPlaybackToken({
        resourceId: vod_id,
        resourceType: 'RESOURCE_TYPE_VOD_EVENT'
      });
      const token = playbackToken?.token;
      return buildPlayableVideoData({
        vod,
        download_uri,
        token,
        data: {
          fileId: file_id,
          vodId: vod_id
        }
      });
    }
    if (!isVODStatusFailed(vod?.status)) {
      if (download_uri) {
        return buildPlayableVideoData({
          vod,
          download_uri,
          token: undefined,
          data: {
            fileId: file_id,
            vodId: vod_id
          }
        });
      }
    }
    return undefined;
  }));
  return videoData.filter((x): x is TrailerViewVideoData => Boolean(x));
};
const useInitialTrailerViewMedia = roomId => {
  const $ = _c(29);
  let t0;
  if ($[0] !== roomId) {
    t0 = () => fetchTrailerMedia(roomId).then(_temp);
    $[0] = roomId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data: trailerMedia,
    isLoading: isMediaLoading
  } = useSWR(roomId ? `fetchTrailerMedia-useInitialTrailerViewMedia-${roomId}` : null, t0);
  const videoSlotCount = trailerMedia?.trailer_videos?.length || 0;
  let t1;
  if ($[2] !== videoSlotCount) {
    t1 = Array.from({
      length: videoSlotCount
    });
    $[2] = videoSlotCount;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1 || $[5] !== trailerMedia?.trailer_videos) {
    t2 = t1.map((_, idx) => ({
      fileId: trailerMedia?.trailer_videos?.[idx]?.file_id || "",
      vodId: trailerMedia?.trailer_videos?.[idx]?.vod_id || "",
      url: "",
      playbackToken: undefined,
      coverImage: "",
      vodStatus: "",
      isConverted: false,
      type: MEDIA_TYPE.VIDEO,
      isPlaceholder: true,
      key: trailerMedia?.trailer_videos?.[idx]?.vod_id || `video-placeholder-${idx}`
    }));
    $[4] = t1;
    $[5] = trailerMedia?.trailer_videos;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const videoPlaceholders = t2;
  let t3;
  if ($[7] !== trailerMedia) {
    t3 = trailerMedia?.trailer_videos && Array.isArray(trailerMedia.trailer_videos) ? ["convertInitialVideos", trailerMedia.trailer_videos] : null;
    $[7] = trailerMedia;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== trailerMedia?.trailer_videos) {
    t4 = () => convertInitialVideos(trailerMedia?.trailer_videos ?? []);
    $[9] = trailerMedia?.trailer_videos;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      revalidateOnFocus: false
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    data: t6,
    isLoading: isVideoLoading
  } = useSWR(t3, t4, t5);
  let t7;
  if ($[12] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const videos = t7;
  let t8;
  if ($[14] !== trailerMedia?.trailer_videos || $[15] !== videoPlaceholders || $[16] !== videos) {
    t8 = videoPlaceholders.map((placeholder, idx_0) => {
      const video = videos.find(v => v.vodId === trailerMedia?.trailer_videos?.[idx_0]?.vod_id);
      if (video) {
        return {
          ...video,
          isPlaceholder: false,
          key: video.vodId || `video-${idx_0}`
        };
      }
      return placeholder;
    });
    $[14] = trailerMedia?.trailer_videos;
    $[15] = videoPlaceholders;
    $[16] = videos;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const filledVideos = t8;
  const isImagesLoading = !trailerMedia?.trailer_images && isMediaLoading;
  const imageSlotCount = trailerMedia?.trailer_videos?.length || 0;
  let t9;
  if ($[18] !== imageSlotCount) {
    t9 = imageSlotCount > 0 ? Array.from({
      length: 1
    }).map(_temp2) : [];
    $[18] = imageSlotCount;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const imagePlaceholders = t9;
  let t10;
  if ($[20] !== imagePlaceholders || $[21] !== isImagesLoading || $[22] !== trailerMedia) {
    t10 = !isImagesLoading && Array.isArray(trailerMedia?.trailer_images) ? trailerMedia.trailer_images.map(_temp3) : imagePlaceholders;
    $[20] = imagePlaceholders;
    $[21] = isImagesLoading;
    $[22] = trailerMedia;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const images = t10;
  let t11;
  if ($[24] !== filledVideos || $[25] !== images || $[26] !== isMediaLoading || $[27] !== isVideoLoading) {
    t11 = {
      videos: filledVideos,
      images,
      isMediaLoading,
      isVideoLoading
    };
    $[24] = filledVideos;
    $[25] = images;
    $[26] = isMediaLoading;
    $[27] = isVideoLoading;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
};
export default useInitialTrailerViewMedia;
function _temp(res) {
  return res?.results;
}
function _temp2(__0, idx_1) {
  return {
    url: "",
    duration: CAROUSEL_DURATION_MS,
    type: MEDIA_TYPE.IMAGE,
    isPlaceholder: true,
    key: `image-placeholder-${idx_1}`
  };
}
function _temp3(img, idx_2) {
  return {
    ...buildImageData(img),
    isPlaceholder: false,
    key: img.url || `image-${idx_2}`
  };
}